import React, { useCallback } from 'react';
import { Anchor, Text, Group, Tooltip } from '@mantine/core';

const POLICYRULE_VIEW_ROUTE = '#admin/policyrule/view';
const LOOSE_REF_WARNING = 'Loose reference; policy rule may no longer exist.';

interface PolicyRuleIdLinkProps {
    policyRuleId: string;
    /** When false, render as plain text (e.g. policy rule type not in entitlements). */
    canNavigate: boolean;
    /** Optional display name (e.g. PolicyRuleDisplayName); shown as link text when present. */
    policyRuleDisplayName?: string;
}

/**
 * Renders PolicyRuleId as a link to the PolicyRule entity view when the user can navigate to admin/policyrule.
 * Optionally shows PolicyRuleDisplayName as the link label for readability.
 */
export const PolicyRuleIdLink: React.FC<PolicyRuleIdLinkProps> = ({
    policyRuleId,
    canNavigate,
    policyRuleDisplayName
}) => {
    const linkLabel = (policyRuleDisplayName ?? '').trim() || policyRuleId;
    const route = policyRuleId ? `${POLICYRULE_VIEW_ROUTE}/${encodeURIComponent(policyRuleId)}` : null;

    const handleClick = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        if (route) window.location.hash = route;
    }, [route]);

    const debugProps = {
        'data-policy-rule-id': policyRuleId,
        'data-policy-rule-id-can-navigate': canNavigate,
        'data-policy-rule-id-route': route ?? undefined
    };

    if (!canNavigate) {
        return (
            <Text component="span" style={{ wordBreak: 'break-word' }} {...debugProps}>
                {linkLabel}
            </Text>
        );
    }

    if (!route) {
        return (
            <Text component="span" style={{ wordBreak: 'break-word' }} {...debugProps}>
                {linkLabel}
            </Text>
        );
    }

    return (
        <Group gap={4} align="center" wrap="nowrap" {...debugProps}>
            <Anchor href={route} onClick={handleClick} underline="hover" style={{ wordBreak: 'break-word' }} title={linkLabel !== policyRuleId ? policyRuleId : undefined}>
                {linkLabel}
            </Anchor>
            <Tooltip label={LOOSE_REF_WARNING}>
                <Text component="span" c="dimmed" style={{ cursor: 'help' }}>*</Text>
            </Tooltip>
        </Group>
    );
};
